
import React, {FC, MouseEvent, useState, useRef, useEffect, ChangeEvent, MutableRefObject}  from 'react';
import {useParams} from 'react-router';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { Controller, FieldArray, FieldArrayMethodProps, FormProvider, useForm } from 'react-hook-form';
import { useTranslation  } from 'react-i18next';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';

import { IAppLogo, IAppTheme, defaultAppTheme, defaultAppLogo, typographyGroupBoxStyling, carouselImage } from 'themes/commonStyles';

import { IPayrollProcessing, defaultPayrollProcessing } from './models/PayrollProcessing';

import ArrayFieldTableEx, { ActionIconTableRow, HeadCell } from 'components/ui/ArrayFieldTableEx';

import entityService, { useBasicFilterEntity } from 'features/services/Entity';
import { currentBasicTextFilterPropsAtom, currentFormNameAtom, isSearchBoxShowAtom, isSaveLoadingAtom } from 'library/store';
import usePayrollProcessingService, { useBasicFilterPayrollProcessing } from './services/PayrollProcessing';
import { useRecoilState } from 'recoil';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { IEntity, IResult } from 'library/interface';
import Button from '@mui/material/Button';
import { Checkbox, FormControlLabel, IconButton, InputAdornment, MenuItem, Typography } from '@mui/material';
import NumberFormat from 'react-number-format';

import { DatePicker } from '@mui/x-date-pickers';

import TextFieldRight from 'components/ui/TextFieldRight';
import { FormDialog } from 'components/ui/FormDialog';
import { BasicTextFilterForm } from 'components/ui/BasicTextFilterForm';
import useConfigurationService, { useBasicFilterEnumeration } from 'features/configuration/services/Enumeration';
import {IEnumerationItem} from 'features/configuration/models/Enumeration';

export const PayrollProcessingForm: FC<IPayrollProcessing> = (props: IPayrollProcessing = defaultPayrollProcessing) => {

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();  
  const { enqueueSnackbar } = useSnackbar();

  const {id} = useParams();
  
  const [_id, _setId] = useState<number>( Number( id || 0 ) );

  const {retrieveEntity, retrieveData, openEntityActionDrawer, checkEntitySaveAuthorization } = entityService();

  const { createPayrollProcessing, updatePayrollProcessing } = usePayrollProcessingService();

  const [currentFormName, setCurrentFormNameAtom] = useRecoilState(currentFormNameAtom);
  const [isSaveLoading, setIsSaveLoading] = useRecoilState(isSaveLoadingAtom);

  const [isSearchBoxShow, setIsSearchBoxShow] = useRecoilState(isSearchBoxShowAtom);
  const [currentBasicTextFilterProps, setCurrentBasicTextFilterProps] = useRecoilState(currentBasicTextFilterPropsAtom);
  const basicFilterPayrollProcessing = useBasicFilterPayrollProcessing( 
    (event: React.MouseEvent<unknown>, row: IPayrollProcessing) => {
        setIsSearchBoxShow(false);
        _setId(row.id);
      }
  );

  

 

  const methods = useForm<IPayrollProcessing>({defaultValues:defaultPayrollProcessing});
  const { register, setValue ,getValues, watch, reset ,handleSubmit ,control , formState: { errors } } = methods;

  const watchType = watch('type');

  const queryClient = useQueryClient();
  const {isLoading, isError, isSuccess ,error,mutate } = useMutation<IResult<IPayrollProcessing>,Error,IPayrollProcessing>(
      _id>0?updatePayrollProcessing:createPayrollProcessing, {   
        onSuccess: (data: IResult<IPayrollProcessing>) => {
          enqueueSnackbar( t('_Operation_done'), { variant: 'success',
                anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1000 }); 
                   
          setIsSaveLoading(false);
          _setId(data.data.id);
          //setCurrentEntityIdForAction(data.data.id);
          
          queryClient.invalidateQueries(['PayrollProcessing',data.data.id]);
        },
        onError: (err: Error) => {          
          enqueueSnackbar( error?.message, { variant: 'error',
                anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 2000 });
          setIsSaveLoading(false);
        }
      });

    const {data: _data, refetch} = useQuery<IPayrollProcessing>(['PayrollProcessing', _id], () => retrieveEntity('PayrollProcessing',_id), 
      {refetchOnWindowFocus: false ,enabled: false } );

      const handlePayrollProcessing = ( event: React.MouseEvent<HTMLElement>, _newPayrollProcessing: 'month'|'week'|'day'|'year' ) => {
        if(_newPayrollProcessing === null) return;
          setValue('type', _newPayrollProcessing);    
        };


      const [headPayrollCells, setHeadPayrollCells]  = useState<HeadCell<IPayroll>[]>([]);
      useEffect(() => {
      setHeadPayrollCells([            
        {id:'id', label : t('Id'),  display: false, type: 'numeric', },
        
        {id:'employeeFullName', label : t('Employee'),  display: true, type: 'string', width: 40,},
        
        // {id:'defaultAmount', label : t('Amount'),  display: true, type: 'numeric', width: 20, 
        //     isEditable: cellEditableEmployeeSalaryItem},
        // {id:'amountExpression', label : `${t('Expression')} - ${t('Amount')}`,  display: true, type: 'string', 
        //     width: 40, getInputAdornment: getInputAdornmentEmployeeSalaryItem},
      ]  )
    }, [t,i18n])
  
    const refAppendPayrolls = useRef<(value: Partial<FieldArray<IPayrollProcessing>> | Partial<FieldArray<IPayrollProcessing>>[], options?: FieldArrayMethodProps) => void>(null);
    const refUpdatePayroll = useRef<(index: number,value: Partial<FieldArray<IPayrollProcessing>> ) => void>(null);
    const refRemovePayroll = useRef<(index: number ) => void>(null);
  
    const [payrollIndex, setPayrollIndex] = useState<number>(-1);
    const payrollRowActionIcon = ( payroll: IPayroll) : ActionIconTableRow<IPayrollProcessing,IPayroll> => {
    
      const res: ActionIconTableRow<IPayrollProcessing,IPayroll> = {
        toolTip: 'Remove',
        icon: RemoveCircleIcon,
        hasAction: (index: number,row: IPayroll) => true, // ((optionPropertyName1 || '') !== '') || ((optionPropertyName2 || '') !== '') || ((optionPropertyName3 || '') !== ''),
        isActionExecuting: true,
        onRowClickIcon: (event : any,index: number, row: IPayroll) => {
          
          (refRemovePayroll.current??emptyFunc)(index);            
        }
      }
      return res;
    }


      useEffect( () => {        
        setCurrentFormNameAtom(t('Payroll processing'));
        setCurrentBasicTextFilterProps(basicFilterPayrollProcessing);
      }, []);    
    
      /********** This use effect call retreive data wich will call refetch and _data will be updated. 
        and the new useEffect will take place ********************/
        useEffect( () => {
            // setCurrentFormName(t('Billing'));        
            
            if(_id > 0)
              retrieveData('PayrollProcessing',_id, refetch);  
          }, [_id] );
    
    
        useEffect( () => {
            
        if(_data && _data.id > 0) {
            reset(_data);
        }
        }, [_data]);
    
      const newData = async (event: MouseEvent<HTMLButtonElement>) => {    
        _setId(0);      
        reset(defaultPayrollProcessing);    
      }
      
      const saveData = async (event: MouseEvent<HTMLButtonElement>) => {     
        if(!checkEntitySaveAuthorization('PayrollProcessing', _id)) {
          setIsSaveLoading(false);         
          return;
        }

          const data = getValues(); 
          // if(data.name.trim() === '' || data.description.trim() === '') {
          //     enqueueSnackbar( t('Reference is not specified'), { variant: 'warning',
          //           anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
          //           setIsSaveLoading(false);
          //           return;
          //   }
      
          mutate(data);
      }
    
      const actionData = async (event: MouseEvent<HTMLButtonElement>) => {
        openEntityActionDrawer('PayrollProcessing', _id);
      }
      
    const afterAction = async (event: MouseEvent<HTMLButtonElement>) => {          
    //    queryClient.invalidateQueries(['RequestType',currentEntityIdForAction]);        
    //    await retreiveData(currentEntityNameForAction,currentEntityIdForAction, refetch);        
    //    reset(_data);        
    }

  return (
    <FormProvider {...methods} >
            <Box sx={{ mx: 0.1 }}>
                <Grid container rowSpacing={3} columnSpacing={3}>
                    <Grid item xs={12} sm={6} md={4} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >                        
                        <Stack flexDirection='column'  >
                            <Box sx={{ mt: 1, width: '100%' }} >
                                <Button id='btnNew' onClick={newData} sx={ {display:'none'}}  />                                  
                                <Button id='btnSave' onClick={saveData} sx={ {display:'none'}}  />
                                <Button id='btnAction' onClick={actionData} sx={ {display:'none'}}  />                                                              
                                <Button id='btnAfterAction' onClick={afterAction} sx={ {display:'none'}}  />

                                {/* <TextField sx={{width:'calc(5% - 8px)'}} id="id" label="Id" {...register('id')} inputProps={ {readOnly: true}} /> 
                                <TextField sx={{width:'calc(20% - 8px)'}} id="name" label={t('Name')} {...register('name')} inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } } />
                                <TextField sx={{width:'calc(35% - 8px)'}} id="description" label={t('Description')} {...register('description')} />
                                 */}
                            </Box>
                            <Box sx={{ mt: 1, width: '100%' }} >
                              <ToggleButtonGroup value={watchType} exclusive key={watchType} size="small" onChange={handlePayrollProcessing} aria-label="text alignment" fullWidth >
                                <ToggleButton value="month" aria-label="centered">
                                  {t('Month')}
                                </ToggleButton>
                                <ToggleButton value="week" aria-label="centered">
                                  {t('Week')}
                                </ToggleButton>       
                                <ToggleButton value="day" aria-label="centered">
                                  {t('Day')}
                                </ToggleButton>       
                                <ToggleButton value="year" aria-label="centered">
                                  {t('Year')}
                                </ToggleButton>        
                              </ToggleButtonGroup>                                                             
                            </Box>
                            <Box sx={{ mt: 1, width: '100%' }} >
                              <Controller control={control}
                                  name='date' 
                                  render={({ field: { onChange, onBlur, value, ref } }) => (
                                    <DatePicker label={t('Operation date')} 
                                      views={ watchType === 'month' ? ["year", "month"] : 
                                              watchType === 'week' ? ["year", "month", "day"] :
                                            watchType === 'day' ? ["year", "month", "day"] : ["year"]
                                      }
                                      onChange={onChange}                     
                                      value={new Date(value)}
                                      slotProps={{ textField: { sx: {width:'calc(33% - 8px)'}  }} }
                                      //renderInput={(params) => <TextField {...params} sx={{width:'calc(20% - 8px)'}} />}
                                    /> )}
                                />
                                <Controller control={control}
                                  name='startDate' 
                                  render={({ field: { onChange, onBlur, value, ref } }) => (
                                    <DatePicker label={t('Start date')} 
                                      onChange={onChange}                     
                                      value={new Date(value)}
                                      slotProps={{ textField: { sx: {width:'calc(33% - 8px)'}  }} }
                                      //renderInput={(params) => <TextField {...params} sx={{width:'calc(20% - 8px)'}} />}
                                    /> )}
                                />
                                <Controller control={control}
                                  name='endDate' 
                                  render={({ field: { onChange, onBlur, value, ref } }) => (
                                    <DatePicker label={t('End date')} 
                                      onChange={onChange}                     
                                      value={new Date(value)}
                                      slotProps={{ textField: { sx: {width:'calc(33% - 8px)'}  }} }
                                      //renderInput={(params) => <TextField {...params} sx={{width:'calc(20% - 8px)'}} />}
                                    /> )}
                                />
                            </Box>
                            <Box sx={{ mt: 0.25, mb: 0.25, width: '100%' }} >
                              <Typography  variant="h6" id="tableTitle" color="primary" noWrap sx={{...typographyGroupBoxStyling}}>
                                {`${t('Information')} ... ##`}
                              </Typography>                              
                            </Box>
                            <Box sx={{ mt: 0.25, mb: 0.25, width: '100%' }} >
                              <Controller control={control}
                                  name='issueDate' 
                                  render={({ field: { onChange, onBlur, value, ref } }) => (
                                    <DatePicker label={t('Issue date')} 
                                      onChange={onChange} disabled                    
                                      value={new Date(value)}
                                      slotProps={{ textField: { sx: {width:'calc(33% - 8px)'}  }} }
                                      //renderInput={(params) => <TextField {...params} sx={{width:'calc(20% - 8px)'}} />}
                                    /> )}
                                />                              
                            </Box>
                            <Box sx={{ mt: 0.25, mb: 0.25, width: '100%' }} >
                              <FormControlLabel sx={{width:'calc(33% - 8px)'}}
                                  label={`${t('Cancelled ?')}`}
                                  control={
                                  <Controller
                                      name='isCancelled'
                                      control={control}
                                      render={({field: {value, onChange,...props} }) => <Checkbox {...props} checked={value} onChange={onChange} />}                        
                              />} />
                              <Controller control={control}
                                  name='cancellationDate' 
                                  render={({ field: { onChange, onBlur, value, ref } }) => (
                                    <DatePicker label={t('Issue date')} 
                                      onChange={onChange} disabled                    
                                      value={new Date(value)}
                                      slotProps={{ textField: { sx: {width:'calc(33% - 8px)'}  }} }
                                      //renderInput={(params) => <TextField {...params} sx={{width:'calc(20% - 8px)'}} />}
                                    /> )}
                                />                              
                            </Box>
                        </Stack>                        
                    </Grid>                    
                    <Grid item xs={12} sm={6} md={8} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >
                      <Stack flexDirection='column'>
                        <Box sx={{ mt: 1, width: '100%' }} >
                            <ArrayFieldTableEx<IPayrollProcessing,IPayroll,'id'> 
                                mainObject={getValues()} fieldKey='id' 
                                headCells={headPayrollCells} rowsPathName={`payrolls` }
                                title={t('Salary by employee')} rowActionIcon={payrollRowActionIcon}  
                                //onRowSelected={handleQuerySelected}
                                                    
                                refAppend={refAppendPayrolls as MutableRefObject<(value: Partial<FieldArray<IPayrollProcessing>> | Partial<FieldArray<IPayrollProcessing>>[], options?: FieldArrayMethodProps) => void>}
                                refUpdate={refUpdatePayroll as MutableRefObject<(index: number,value: Partial<FieldArray<IPayrollProcessing>>) => void>}
                                refRemove={refRemovePayroll as MutableRefObject<(index: number) => void>}

                                //stateSelected={[selectedRoleEntities, setSelectedRoleEntities]}
                                //displayMore={undefined}
                                toolbarActions={[
                                //{ toolTip: `${t('Add')}...`, onClickIcon: handleAddNewBillings ,icon: AddCircleIcon,  },
                                //{ toolTip: `${t('Add')}...`, onClickIcon: handleAddPayrollProcessings ,icon: AddCircleIcon,  },

                                ]}
                              /> 
                          </Box>
                        </Stack>
                    </Grid> 
                </Grid>
            </Box>
        </FormProvider> 
  )
}

